import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import {
  Card,
  CardContent,
} from "../../ui/card";
import { Badge } from "../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { TextareaUI } from "ui/textareaUI";
import {
  Plus,
  Search,
  Edit2,
  Briefcase,
  Check,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  getTourOperatorTypes,
  TourOperatorTypeApi,
  createTourOperatorType,
  updateTourOperatorType,
  getTourOperatorTypeSummary,
} from "../../services/tourOperatorTypeService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface TOType {
  id: string;
  name: string;
  code: string;
  category: "in-house" | "external";
  releaseDays: number;
  description: string;
  status: "active" | "inactive";
}

export const TourOperatorType = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<
    "name" | "code" | "category" | null
  >(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  // Advanced filters
  const [filters, setFilters] = useState({
    name: "",
    code: "",
    category: "all" as "all" | "in-house" | "external",
    status: "all" as "all" | "active" | "inactive",
  });

  const [toTypes, setToTypes] = useState<TOType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { canAdd, canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  const displayedTypes = toTypes;

  const [summary, setSummary] = useState({
    totalCount: 0,
    activeCount: 0,
    inHouseCount: 0,
    externalCount: 0,
  });

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    category: "external" as "in-house" | "external",
    releaseDays: 0,
    description: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    category?: string;
    description?: string;
  }>({});

  const mapApiToUI = (api: TourOperatorTypeApi): TOType => ({
    id: String(api.tourOperatorTypeId),
    name: api.typeName,
    code: api.code,
    category: api.category,
    releaseDays: api.releaseDays,
    description: api.description,
    status: api.isActive ? "active" : "inactive",
  });

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await getTourOperatorTypeSummary();
      setSummary(res);
    } catch {
      toast.error("Failed to load summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({
      title: screenName,
      subtitle: "Manage tour operator types and categories",
      icon: <ScreenIcon className="h-4 w-4" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);


  useEffect(() => {
    const loadTypes = async () => {
      try {
        setLoading(true);
        const res = await getTourOperatorTypes({
          searchText: searchTerm || null,
          typeName: filters.name || null,
          code: filters.code || null,
          category: filters.category === "all" ? null : filters.category,
          isActive:
            filters.status === "all" ? null : filters.status === "active",
          pageNo,
          pageSize,
        });

        setToTypes(res.items.map(mapApiToUI));
        setTotalCount(res.totalCount);
      } catch {
        toast.error("Failed to load tour operator types");
      } finally {
        setLoading(false);
      }
    };

    loadTypes();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, pageNo, pageSize]);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const res = await getTourOperatorTypeSummary();
        setSummary(res);
      } catch {
        toast.error("Failed to load summary");
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm, filters]);

  const handleSort = (field: "name" | "code" | "category") => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Apply sorting
  const sortedToTypes = [...displayedTypes].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;

    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleClearFilters = () => {
    setFilters({
      name: "",
      code: "",
      category: "all",
      status: "all",
    });
    toast.success("Filters cleared");
  };

  const isDuplicateName = (name: string) => {
    return toTypes.some(
      (t) =>
        t.name.toLowerCase() === name.trim().toLowerCase() && t.id !== editingId
    );
  };

  const isDuplicateCode = (code: string) => {
    const c = code.trim().toUpperCase();
    return toTypes.some(
      (t) => t.code.trim().toUpperCase() === c && t.id !== editingId
    );
  };

  const sanitizeTypeName = (value: string) => {
    return value.replace(/[^A-Za-z -]/g, "");
  };

  const sanitizeCode = (value: string) => {
    return value
      .toUpperCase()
      .replace(/[^A-Z-]/g, "");
  };

  const sanitizeDescription = (value: string) => {
    return value.replace(/[^A-Za-z0-9 -]/g, "");
  };

  const handleSave = async () => {
    const newErrors: typeof errors = {};

    // Type Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Type name is required";
    } else if (!/^[A-Za-z -]+$/.test(formData.name)) {
      newErrors.name =
        "Type name can contain only letters, spaces, and hyphens";
    } else if (isDuplicateName(formData.name)) {
      newErrors.name = "Type name already exists";
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (!/^[A-Z-]+$/.test(formData.code)) {
      newErrors.code =
        "Code can contain only letters and hyphens";
    } else if (formData.code.length > 15) {
      newErrors.code = "Code must not exceed 15 characters";
    } else if (isDuplicateCode(formData.code)) {
      newErrors.code = "Code already exists";
    }

    // Description validation
    if (
      formData.description &&
      !/^[A-Za-z0-9 -]+$/.test(formData.description)
    ) {
      newErrors.description =
        "Description can contain only letters, numbers, spaces, and hyphens";
    }

    // Category validation
    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingId) {
        await updateTourOperatorType(Number(editingId), {
          tourOperatorTypeId: Number(editingId),
          typeName: formData.name,
          code: formData.code,
          category: formData.category,
          releaseDays: formData.releaseDays,
          description: formData.description,
          isActive: formData.status === "active",
          updatedBy: 1,
        });

        toast.success("Tour Operator Type updated successfully");
      } else {
        await createTourOperatorType({
          typeName: formData.name,
          code: formData.code,
          category: formData.category,
          releaseDays: formData.releaseDays,
          description: formData.description,
          isActive: formData.status === "active",
          createdBy: 1,
        });

        toast.success("Tour Operator Type added successfully");
      }

      await reloadTypes();
      await loadSummary();
      handleCloseDialog();
    } catch {
      toast.error("Failed to save tour operator type");
    }
  };

  const reloadTypes = async () => {
    try {
    setLoading(true)
      const res = await getTourOperatorTypes({    
        searchText: searchTerm || null,
        typeName: filters.name || null,
        code: filters.code || null,
        category: filters.category === "all" ? null : filters.category,
        isActive: filters.status === "all" ? null : filters.status === "active",
        pageNo,
        pageSize,
      });

      setToTypes(res.items.map(mapApiToUI));
      setTotalCount(res.totalCount);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type: TOType) => {
    setEditingId(type.id);
    setFormData({
      name: type.name,
      code: type.code,
      category: type.category,
      releaseDays: type.releaseDays,
      description: type.description,
      status: type.status,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setErrors({});
    setFormData({
      name: "",
      code: "",
      category: "external",
      releaseDays: 0,
      description: "",
      status: "active",
    });
  };

  const getCategoryColor = (category: string) => {
    return category === "in-house"
      ? "bg-purple-100 text-purple-700 border-purple-300"
      : "bg-blue-100 text-blue-700 border-blue-300";
  };

  const hasActiveFilters =
    filters.name ||
    filters.code ||
    filters.category !== "all" ||
    filters.status !== "all";

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by name, code, or category..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(1);
                }}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`shrink-0 ${
                hasActiveFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
              {hasActiveFilters && (
                <Badge className="ml-2 bg-[#5B9BD5] text-white text-xs px-1.5 py-0">
                  {
                    [
                      filters.name,
                      filters.code,
                      filters.category !== "all",
                      filters.status !== "all",
                    ].filter(Boolean).length
                  }
                </Badge>
              )}
            </Button>
            {canAdd && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add TO Type
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Types</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {summary.totalCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Active</p>
                    <p className="text-2xl font-bold text-green-900">
                      {summary.activeCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">In-House</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {summary.inHouseCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">External</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {summary.externalCount}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <Card className="border-[#5B9BD5]/30 bg-sky-50/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#5B9BD5] flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Advanced Filters
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-gray-600 hover:text-gray-900 h-8"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-gray-700">Type Name</Label>
                  <Input
                    placeholder="Filter by name"
                    value={filters.name}
                    onChange={(e) =>
                      setFilters({ ...filters, name: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-gray-700">Code</Label>
                  <Input
                    placeholder="Filter by code"
                    value={filters.code}
                    onChange={(e) =>
                      setFilters({ ...filters, code: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-gray-700">Category</Label>
                  <Select
                    value={filters.category}
                    onValueChange={(value: "all" | "in-house" | "external") =>
                      setFilters({ ...filters, category: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="in-house">In-House</SelectItem>
                      <SelectItem value="external">External</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-gray-700">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value: "all" | "active" | "inactive") =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50 w-[25%]"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Type Name
                        {sortField === "name" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50 w-[15%]"
                      onClick={() => handleSort("code")}
                    >
                      <div className="flex items-center gap-1">
                        Code
                        {sortField === "code" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50 w-[15%]"
                      onClick={() => handleSort("category")}
                    >
                      <div className="flex items-center gap-1">
                        Category
                        {sortField === "category" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-[10%]"
                    >
                      Release Days
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-[30%]"
                    >
                      Description
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs w-[10%]"
                    >
                      Status
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right w-[8%]"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedToTypes.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 px-4 text-[#5B9BD5] font-medium"
                      >
                        Loading To Types...
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedToTypes.map((type) => (
                      <TableRow key={type.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Briefcase className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <div className="relative group max-w-[150px]">
                              <p className="text-xs text-gray-700 truncate">
                                {type.name}
                              </p>
                              <div
                                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-max 
                                  max-w-[420px] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg whitespace-normal"
                              >
                                {type.name}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-[10px] px-1.5 py-0.5"
                          >
                            {type.code}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            className={`${getCategoryColor(type.category)} text-[10px] px-1.5 py-0.5`}
                          >
                            {type.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            variant="outline"
                            className="border-amber-200 bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0.5"
                          >
                            {type.releaseDays} {type.releaseDays === 1 ? "day" : "days"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2 max-w-[280px]">
                          <div className="relative group">
                            <p className="text-xs text-gray-700 line-clamp-2 truncate">
                              {type.description}
                            </p>
                            <div
                              className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-max 
                                max-w-[420px] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg whitespace-normal"
                            >
                              {type.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <Badge
                            className={`${type.status === "active" ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {type.status}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(type)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit type"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {sortedToTypes.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 text-sm">
                  No tour operator types found
                </div>
              ) : (
                sortedToTypes.map((type) => (
                  <div
                    key={type.id}
                    className="p-4 hover:bg-sky-50/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-5 h-5 text-[#5B9BD5]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900">
                            {type.name}
                          </h3>
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-[10px] px-1.5 py-0.5 mt-1"
                          >
                            {type.code}
                          </Badge>
                        </div>
                      </div>
                      <Badge
                        className={`${type.status === "active" ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5 flex-shrink-0`}
                      >
                        {type.status}
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">Category:</span>
                        <Badge
                          className={`${getCategoryColor(type.category)} text-[10px] px-1.5 py-0.5`}
                        >
                          {type.category}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">Release Days:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {type.releaseDays} {type.releaseDays === 1 ? "day" : "days"}
                        </span>
                      </div>
                      {type.description && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">
                            Description:
                          </p>
                          <p className="text-xs text-gray-900">
                            {type.description}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(type)}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="tour operator types"
          />
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingId ? "Edit Tour Operator Type" : "Add Tour Operator Type"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update tour operator type details"
                  : "Add a new tour operator type"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Type Name"
                    required
                    id="name"
                    placeholder="e.g., Tour Operator"
                    value={formData.name}
                    onChange={(e) => {
                      const value = sanitizeTypeName(e.target.value);
                      setFormData({ ...formData, name: value });
                      setErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    className="text-sm"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Code"
                    required
                    id="code"
                    placeholder="e.g., EXT-TO"
                    value={formData.code}
                    onChange={(e) => {
                      const value = sanitizeCode(e.target.value);

                      if (value.length <= 15) {
                        setFormData({ ...formData, code: value });
                        setErrors((prev) => ({ ...prev, code: undefined }));
                      }
                    }}
                  />
                  {errors.code && (
                    <p className="text-xs text-red-600">{errors.code}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <SelectFieldset label="Category" required>
                    <Select
                      value={formData.category}
                      onValueChange={(value: "in-house" | "external") => {
                        setFormData({ ...formData, category: value });
                        setErrors((prev) => ({ ...prev, category: undefined }));
                      }}
                    >
                      <SelectTriggerUI id="category" className="text-sm">
                        <SelectValue />
                      </SelectTriggerUI>
                      <SelectContent>
                        <SelectItem value="in-house">In-House</SelectItem>
                        <SelectItem value="external">External</SelectItem>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {errors.category && (
                    <p className="text-xs text-red-600">{errors.category}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Release Days"
                    required
                    id="releaseDays"
                    type="number"
                    min={0}
                    value={formData.releaseDays}
                    onChange={(e) => {
                      const value = Math.max(0, Number(e.target.value));
                      setFormData({
                        ...formData,
                        releaseDays: value,
                      });
                    }}
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Description">
                  <TextareaUI
                    id="description"
                    placeholder="Describe the tour operator type..."
                    value={formData.description}
                    onChange={(e) => {
                      const value = sanitizeDescription(e.target.value);
                      setFormData({ ...formData, description: value });
                    }}
                    className="text-sm min-h-[100px]"
                  />
                </SelectFieldset>
                {errors.description && (
                  <p className="text-xs text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI id="status" className="text-sm">
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseDialog}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm"
              >
                {editingId ? "Update" : "Add"} Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
